import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import {
  Loader2, ExternalLink, Check, RefreshCw, ChevronLeft, ChevronRight,
  Calendar as CalendarIcon, Mail, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Newsletter → Recommended articles panel.
 *
 * Data flow:
 *   1. Fetch `/newsletter/candidates` with the active range. The
 *      backend returns rows immediately with the LAST-KNOWN cached
 *      engagement plus a `stale_article_ids` list.
 *   2. If `stale_article_ids` is non-empty, fire a background
 *      `/newsletter/engagement/refresh` call for JUST those IDs and
 *      merge fresh numbers into local state as they arrive.
 *   3. Refresh button ignores the 24h TTL and refreshes every article
 *      in the current range.
 *
 * State kept locally (the parent only cares about which article_ids
 * are selected, exposed via `onToggle`):
 *   - `days` / `customSince` / `customUntil` → query params
 *   - `sortBy` → "engagement" | "date"
 *   - `page`   → 1-indexed
 *   - `engagementOverrides` → { article_id: {engagement, fetched_at} }
 *     applied on top of the fetched candidate list so a mid-flight
 *     refresh call doesn't have to invalidate the whole react-query
 *     cache.
 *
 * Persistence: the range is saved to `localStorage` under
 * `newsletter.range` and rehydrated on mount, falling back to 7-days.
 */

const PRESET_DAYS = [3, 4, 7, 14, 30];
const PAGE_SIZE = 15;
const STORAGE_KEY = "newsletter.range.v1";

/**
 * The four sortable columns, in button order.
 *
 * `pill` and `active` live side by side on purpose: the editor asked
 * for the sort button to carry the colour of the pill it sorts by, so
 * both class strings are read from THIS table and nowhere else. Add a
 * metric here and the pill, the button, the comparator and the
 * subtitle all pick it up together.
 *
 * `metric` returns the raw value for the comparator. A null means "we
 * have no number for this article" (never "zero") and always sorts
 * last — see `sortedCandidates`.
 */
const SORT_OPTIONS = [
  {
    key: "jetpack",
    label: "Jetpack Views",
    testId: "newsletter-sort-jetpack",
    pill: "bg-amber-50 text-amber-800 border border-amber-200",
    active: "bg-amber-600 text-white",
    subtitle: "sorted by website views (Jetpack)",
    metric: (c) => c.jetpack_views,
  },
  {
    key: "fbviews",
    label: "Facebook Views",
    testId: "newsletter-sort-fbviews",
    pill: "bg-indigo-50 text-indigo-800 border border-indigo-200",
    active: "bg-indigo-600 text-white",
    subtitle: "sorted by Facebook views",
    metric: (c) => c.views,
  },
  {
    key: "engagement",
    label: "Engagement",
    testId: "newsletter-sort-engagement",
    pill: "bg-emerald-50 text-emerald-800 border border-emerald-200",
    active: "bg-emerald-600 text-white",
    subtitle: "sorted by Facebook engagement (reactions + comments)",
    metric: (c) => c.engagement,
  },
  {
    key: "date",
    label: "Date",
    testId: "newsletter-sort-date",
    // Date has no pill, so it keeps the original neutral fill.
    pill: "",
    active: "bg-[var(--ink)] text-white",
    subtitle: "sorted by publish date (newest first)",
    metric: null,
  },
];

const SORT_BY_KEY = Object.fromEntries(SORT_OPTIONS.map((o) => [o.key, o]));
const PILL_MUTED =
  "bg-[var(--secondary)] text-[var(--ink-muted)] border border-[var(--border)]";

/**
 * One metric pill. `value == null` means unavailable and renders an em
 * dash; a real 0 renders as 0 in the muted style. That distinction is
 * load-bearing — a zero that actually means "no data" would quietly
 * bury a story at the bottom of a views sort.
 */
function MetricPill({ value, classes, title, testId }) {
  const lit = value != null && value > 0;
  return (
    <span
      data-testid={testId}
      title={title}
      className={`shrink-0 text-[10px] font-mono-display font-semibold px-1.5 py-0.5 min-w-[28px] text-center ${
        lit ? classes : PILL_MUTED
      }`}
    >
      {value == null ? "—" : value}
    </span>
  );
}

/**
 * Marks an article that has already gone into a newsletter. Previewing
 * never sets this — only a MailerLite draft or an actual send does, so
 * the badge means "committed", not "looked at".
 *
 * `usage` is null for unused articles, which is the common case, so the
 * row stays visually clean unless there's something to say.
 */
function UsageBadge({ usage, clearedAt, articleId, onToggle, busy }) {
  // Unmarked: a muted badge rather than nothing, so the action stays
  // reversible. It reads as unused everywhere else (including the
  // "Hide used" filter) — this is just the undo handle.
  if (!usage) {
    if (!clearedAt) return null;
    return (
      <button
        type="button"
        data-testid={`newsletter-restore-badge-${articleId}`}
        onClick={() => onToggle(false)}
        disabled={busy}
        title={`Unmarked on ${formatShortDate(clearedAt)}. Click to restore its newsletter history.`}
        className={`${PILL_MUTED} shrink-0 inline-flex items-center gap-1 text-[10px] font-mono-display uppercase tracking-wider px-1.5 py-0.5 hover:text-[var(--ink)] disabled:opacity-50`}
      >
        <RotateCcw className="w-3 h-3 shrink-0" />
        unmarked
      </button>
    );
  }
  const sent = usage.status === "sent";
  const total = (usage.sent_count || 0) + (usage.draft_count || 0);
  const when = formatShortDate(usage.last_used_at);
  const parts = [];
  if (usage.sent_count) {
    parts.push(
      `sent in ${usage.sent_count} newsletter${usage.sent_count === 1 ? "" : "s"}`,
    );
  }
  if (usage.draft_count) {
    parts.push(
      `drafted in ${usage.draft_count} newsletter${usage.draft_count === 1 ? "" : "s"}`,
    );
  }
  const title =
    `Already ${parts.join(" and ")}.` +
    (usage.last_subject ? ` Most recent: "${usage.last_subject}"` : "") +
    (when ? ` (${when})` : "");
  return (
    <button
      type="button"
      data-testid={`newsletter-used-badge-${articleId}`}
      onClick={() => onToggle(true)}
      disabled={busy}
      title={`${title} — click to unmark it (the newsletter history itself is not changed).`}
      className={`shrink-0 inline-flex items-center gap-1 text-[10px] font-mono-display uppercase tracking-wider px-1.5 py-0.5 border disabled:opacity-50 ${
        sent
          ? "bg-violet-50 text-violet-800 border-violet-300 hover:bg-violet-100"
          : "bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100"
      }`}
    >
      <Mail className="w-3 h-3 shrink-0" />
      {sent ? "sent" : "drafted"}
      {total > 1 ? ` ×${total}` : ""}
      {when ? ` · ${when}` : ""}
    </button>
  );
}

// ---------------------------------------------------------------
// Small helpers — deliberately kept in-module (only used here).
// ---------------------------------------------------------------

function formatShortDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const opts = { month: "short", day: "numeric" };
  if (d.getFullYear() !== now.getFullYear()) opts.year = "numeric";
  return d.toLocaleDateString(undefined, opts);
}

function formatUpdatedTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

function todayIso() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

// Load persisted range or default to preset=7.
function loadPersistedRange() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { mode: "preset", days: 7, since: "", until: "" };
    const p = JSON.parse(raw);
    if (p?.mode === "custom" && p.since && p.until) return p;
    if (p?.mode === "preset" && PRESET_DAYS.includes(p.days)) return p;
  } catch {
    /* ignore parse errors — fall back to default */
  }
  return { mode: "preset", days: 7, since: "", until: "" };
}

function persistRange(range) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(range));
  } catch {
    /* localStorage disabled — best-effort persistence */
  }
}

export default function RecommendedArticlesPanel({
  selectedIds,   // Set<article_id> — currently in newsletter
  onAdd,         // (candidate) => void
  onRemove,      // (article_id) => void
  onCandidatesChange, // (candidates[]) => void — parent uses this
                      // to drop entries whose article dropped out.
}) {
  const persisted = useMemo(loadPersistedRange, []);
  const [rangeMode, setRangeMode] = useState(persisted.mode);
  const [days, setDays] = useState(persisted.mode === "preset" ? persisted.days : 7);
  const [customSince, setCustomSince] = useState(
    persisted.mode === "custom" ? persisted.since : "",
  );
  const [customUntil, setCustomUntil] = useState(
    persisted.mode === "custom" ? persisted.until : todayIso(),
  );
  const [sortBy, setSortBy] = useState("engagement");
  // "Unused only" filter. Off by default so the list still shows
  // everything in the window unless the editor asks to narrow it.
  const [hideUsed, setHideUsed] = useState(false);
  const [page, setPage] = useState(1);
  // Local overrides layered on top of the query result — keyed by
  // article_id so a `/newsletter/engagement/refresh` response merges
  // in without re-fetching the whole list.
  const [engagementOverrides, setEngagementOverrides] = useState({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  // Tracks the newest fetched_at we've seen — updated as override
  // responses arrive.
  const [lastFetchedAt, setLastFetchedAt] = useState(null);

  // Persist range whenever the user changes it.
  useEffect(() => {
    const snapshot =
      rangeMode === "custom"
        ? { mode: "custom", since: customSince, until: customUntil }
        : { mode: "preset", days };
    persistRange(snapshot);
  }, [rangeMode, days, customSince, customUntil]);

  // Custom range is only "ready" when both dates are set. Guard the
  // query so we don't fire with `since=undefined` and get everything.
  const rangeReady =
    rangeMode !== "custom" || (customSince && customUntil);

  const queryParams = useMemo(() => {
    if (rangeMode === "custom") {
      return { since: customSince, until: customUntil };
    }
    return { days };
  }, [rangeMode, days, customSince, customUntil]);

  const {
    data: candData, isLoading, isFetching, refetch,
  } = useQuery({
    queryKey: ["newsletter-candidates", queryParams],
    queryFn: () => api.newsletterCandidates(queryParams),
    enabled: !!rangeReady,
    placeholderData: keepPreviousData, // v5 replacement for keepPreviousData:true
  });

  const rawCandidates = candData?.candidates || [];
  const staleIds = candData?.stale_article_ids || [];
  // False when the property has no WordPress.com token or no resolvable
  // Jetpack site — the Jetpack sort is disabled rather than offering a
  // sort over a column of em dashes.
  const jetpackConfigured = !!candData?.jetpack_configured;
  const jetpackWindowDays = candData?.jetpack_window_days || 30;

  // Fold overrides into the fetched candidates. Overrides win when
  // present — that's how progressive refresh results propagate.
  const candidates = useMemo(() => {
    if (!rawCandidates.length) return rawCandidates;
    return rawCandidates.map((c) => {
      const o = engagementOverrides[c.article_id];
      if (!o) return c;
      return {
        ...c,
        engagement: o.engagement,
        views: o.views,
        views_available: o.views_available,
        jetpack_views: o.jetpack_views,
        jetpack_views_available: o.jetpack_views_available,
        fetched_at: o.fetched_at,
      };
    });
  }, [rawCandidates, engagementOverrides]);

  // Reset pagination + clear overrides whenever the fetched range
  // changes (react-query keys change). Overrides are keyed by
  // article_id so leaving stale entries in doesn't corrupt anything,
  // but clearing avoids memory bloat over many range changes.
  const queryKeyRef = useRef(null);
  useEffect(() => {
    const key = JSON.stringify(queryParams);
    if (queryKeyRef.current !== key) {
      queryKeyRef.current = key;
      setPage(1);
      setEngagementOverrides({});
    }
  }, [queryParams]);

  // Update lastFetchedAt from the initial fetch, then keep it in sync
  // with overrides (whichever timestamp is newer wins).
  useEffect(() => {
    const initial = candData?.last_fetched_at;
    const overrideTs = Object.values(engagementOverrides)
      .map((o) => o.fetched_at)
      .filter(Boolean);
    const all = [initial, ...overrideTs].filter(Boolean);
    if (!all.length) {
      setLastFetchedAt(null);
      return;
    }
    setLastFetchedAt(all.reduce((a, b) => (a > b ? a : b)));
  }, [candData?.last_fetched_at, engagementOverrides]);

  // Progressive fill-in: when the backend flags rows as stale, fire
  // a refresh call for JUST those IDs and merge results. Guarded by
  // `isRefreshing` so a mid-flight forced refresh isn't clobbered.
  useEffect(() => {
    if (!staleIds.length || isRefreshing) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.refreshNewsletterEngagement(staleIds, false);
        if (cancelled) return;
        mergeEngagementResponse(res);
        if (res.failed_article_ids?.length) {
          toast.message(
            `Facebook engagement missing for ${res.failed_article_ids.length} article(s) — showing last known values.`,
          );
        }
      } catch {
        // Non-fatal — the cached values already rendered. Skip toast
        // so we don't yell at the editor every 30s.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candData]);

  // Bubble the raw fetched candidate list up so the parent can drop
  // `entries` whose article dropped out of the new range. Fires when
  // the query result changes (range switched, new fetch landed) —
  // NOT when in-memory engagement overrides update, because those
  // don't affect which articles are in the list.
  useEffect(() => {
    if (!candData) return;
    onCandidatesChange?.(candData.candidates || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candData]);

  function mergeEngagementResponse(res) {
    const map = res?.engagement || {};
    setEngagementOverrides((prev) => {
      const next = { ...prev };
      Object.entries(map).forEach(([aid, val]) => {
        // Skip entries whose fetch failed AND have no last-known
        // number — keeping the fetched value would overwrite the
        // existing cache with 0.
        if (val.failed && !val.fetched_at) return;
        next[aid] = {
          engagement: val.engagement,
          // `views` is nullable — a null means "insights data
          // unavailable" (page below Meta's 100-follower minimum
          // for insights, missing `read_insights` scope, or the
          // post is < 24h old). Preserve the null so the pill
          // shows an em dash instead of a misleading "0".
          views: val.views ?? null,
          views_available: !!val.views_available,
          // Same nullable contract for the website number. WordPress.com
          // omits a post entirely when it has no rows in the window, so
          // null here is "unknown", not "nobody read it".
          jetpack_views: val.jetpack_views ?? null,
          jetpack_views_available: !!val.jetpack_views_available,
          fetched_at: val.fetched_at,
        };
      });
      return next;
    });
  }

  const byDateDesc = (a, b) =>
    String(b.published_at || "").localeCompare(String(a.published_at || ""));

  // Unmark / restore. Overrides are kept locally so the row flips
  // immediately; the query refetch reconciles afterwards.
  //
  // MUST be declared above `resolveUsage` and the `sortedCandidates`
  // memo below: both read `usageOverrides` during render, and a `const`
  // read before its declaration line is a ReferenceError that blanks
  // the entire page.
  const [usageOverrides, setUsageOverrides] = useState({});
  const [usageBusyId, setUsageBusyId] = useState(null);

  // A locally-unmarked row reads as unused everywhere (filter, count,
  // badge) without waiting for the refetch.
  const resolveUsage = (c) => {
    const o = usageOverrides[c.article_id];
    if (!o) {
      return {
        usage: c.newsletter_usage,
        clearedAt: c.newsletter_usage_cleared_at || null,
      };
    }
    return {
      usage: o.cleared_at ? null : c.newsletter_usage,
      clearedAt: o.cleared_at,
    };
  };

  const sortedCandidates = useMemo(() => {
    // Filter BEFORE sorting so pagination and the "Page N of M" label
    // count only the rows actually on screen.
    const list = hideUsed
      ? candidates.filter((c) => !resolveUsage(c).usage)
      : [...candidates];
    const opt = SORT_BY_KEY[sortBy] || SORT_BY_KEY.engagement;
    if (!opt.metric) {
      list.sort(byDateDesc);
      return list;
    }
    // Metric desc, nulls last, tie-break by date desc. Nulls sink
    // rather than sorting as 0 so "no data yet" never outranks a real
    // low number — an article with 2 views is still a better-known
    // quantity than one with none.
    list.sort((a, b) => {
      const av = opt.metric(a);
      const bv = opt.metric(b);
      const aNull = av == null;
      const bNull = bv == null;
      if (aNull !== bNull) return aNull ? 1 : -1;
      if (!aNull && bv !== av) return bv - av;
      return byDateDesc(a, b);
    });
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidates, sortBy, hideUsed, usageOverrides]);

  const usedCount = useMemo(
    () => candidates.filter((c) => resolveUsage(c).usage).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [candidates, usageOverrides],
  );

  // Reset to page 1 whenever sort or the used-filter changes.
  useEffect(() => setPage(1), [sortBy, hideUsed]);

  const totalPages = Math.max(1, Math.ceil(sortedCandidates.length / PAGE_SIZE));
  const pageStart = (page - 1) * PAGE_SIZE;
  const pageItems = sortedCandidates.slice(pageStart, pageStart + PAGE_SIZE);

  const toggleUsageCleared = async (articleId, cleared) => {
    if (usageBusyId) return;
    setUsageBusyId(articleId);
    try {
      const res = await api.setNewsletterUsageCleared(articleId, cleared);
      setUsageOverrides((prev) => ({
        ...prev,
        [articleId]: {
          cleared_at: res.newsletter_usage_cleared_at || null,
        },
      }));
      toast.success(
        cleared
          ? "Unmarked — it won't show as used in the picker."
          : "Restored its newsletter history.",
      );
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Could not update — try again.",
      );
    } finally {
      setUsageBusyId(null);
    }
  };

  const doRefresh = async () => {
    if (isRefreshing || !candidates.length) return;
    setIsRefreshing(true);
    try {
      const ids = candidates.map((c) => c.article_id);
      const res = await api.refreshNewsletterEngagement(ids, true);
      mergeEngagementResponse(res);
      if (res.failed_article_ids?.length) {
        toast.message(
          `Refreshed. ${res.failed_article_ids.length} article(s) could not be updated — showing last known values.`,
        );
      } else {
        toast.success("Stats refreshed.");
      }
      if (res.jetpack_error) {
        toast.message(
          `Website views unavailable — ${res.jetpack_error}`,
        );
      }
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Refresh failed — try again in a moment.",
      );
    } finally {
      setIsRefreshing(false);
    }
  };

  // --- Subtitle text — reflects active range + active sort. -----------
  const subtitleRange = useMemo(() => {
    if (rangeMode === "custom" && customSince && customUntil) {
      return `Published ${formatShortDate(customSince)} – ${formatShortDate(customUntil)}`;
    }
    return `Published in the last ${days} days`;
  }, [rangeMode, days, customSince, customUntil]);
  const subtitleSort =
    (SORT_BY_KEY[sortBy] || SORT_BY_KEY.engagement).subtitle;

  // --- Range picker ---------------------------------------------------
  const onPresetChange = (v) => {
    if (v === "custom") {
      setRangeMode("custom");
      if (!customSince) {
        // Seed with a reasonable window so the pickers aren't empty.
        const d = new Date();
        d.setDate(d.getDate() - 7);
        setCustomSince(d.toISOString().slice(0, 10));
      }
      if (!customUntil) setCustomUntil(todayIso());
    } else {
      setRangeMode("preset");
      setDays(Number(v));
    }
  };

  const updatedLabel = formatUpdatedTime(lastFetchedAt);

  return (
    <div className="bg-white border border-[var(--border)] p-5 mb-6">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-1">
        <h2 className="font-serif-display text-xl">Recommended articles</h2>
        <div className="flex flex-wrap items-center gap-2">
          {/* Date-range picker */}
          <div
            data-testid="newsletter-range-picker"
            className="inline-flex items-center gap-1"
          >
            <CalendarIcon className="w-3.5 h-3.5 text-[var(--ink-muted)]" />
            <select
              data-testid="newsletter-range-preset"
              value={rangeMode === "custom" ? "custom" : String(days)}
              onChange={(e) => onPresetChange(e.target.value)}
              className="text-[11px] font-mono-display uppercase tracking-wider border border-[var(--border)] bg-white px-2 py-1 outline-none focus:border-[var(--ink)]"
            >
              {PRESET_DAYS.map((d) => (
                <option key={d} value={d}>
                  {d} days
                </option>
              ))}
              <option value="custom">Custom</option>
            </select>
            {rangeMode === "custom" && (
              <>
                <input
                  type="date"
                  data-testid="newsletter-range-since"
                  value={customSince}
                  max={customUntil || todayIso()}
                  onChange={(e) => setCustomSince(e.target.value)}
                  className="text-[11px] border border-[var(--border)] bg-white px-1.5 py-1 outline-none focus:border-[var(--ink)]"
                />
                <span className="text-[11px] text-[var(--ink-muted)]">to</span>
                <input
                  type="date"
                  data-testid="newsletter-range-until"
                  value={customUntil}
                  min={customSince || undefined}
                  max={todayIso()}
                  onChange={(e) => setCustomUntil(e.target.value)}
                  className="text-[11px] border border-[var(--border)] bg-white px-1.5 py-1 outline-none focus:border-[var(--ink)]"
                />
              </>
            )}
          </div>

          {/* Unused-only filter. The point of the marker is spotting an
              older story that never went out and has since picked up
              views, so the filter matters as much as the badge. */}
          <button
            type="button"
            data-testid="newsletter-hide-used-toggle"
            onClick={() => setHideUsed((v) => !v)}
            disabled={!usedCount && !hideUsed}
            className={`inline-flex items-center gap-1 border px-2 py-1 text-[11px] font-mono-display uppercase tracking-wider disabled:opacity-50 disabled:cursor-not-allowed ${
              hideUsed
                ? "bg-[var(--accent)] border-[var(--accent)] text-white"
                : "border-[var(--border)] hover:bg-[var(--secondary)]"
            }`}
            title={
              usedCount
                ? `${usedCount} of ${candidates.length} articles in this range have already been drafted or sent`
                : "No article in this range has been used in a newsletter yet"
            }
          >
            <Mail className="w-3 h-3" />
            {hideUsed ? "Unused only" : `Hide used${usedCount ? ` (${usedCount})` : ""}`}
          </button>

          {/* Refresh Facebook data */}
          <div className="inline-flex items-center gap-2">
            <button
              type="button"
              data-testid="newsletter-refresh-fb"
              onClick={doRefresh}
              disabled={isRefreshing || !candidates.length}
              className="inline-flex items-center gap-1 border border-[var(--border)] px-2 py-1 text-[11px] font-mono-display uppercase tracking-wider hover:bg-[var(--secondary)] disabled:opacity-50 disabled:cursor-not-allowed"
              title="Refetch Facebook engagement and Jetpack website views for the visible range"
            >
              <RefreshCw
                className={`w-3 h-3 ${isRefreshing ? "animate-spin" : ""}`}
              />
              Refresh stats
            </button>
            {updatedLabel && (
              <span
                data-testid="newsletter-updated-time"
                className="text-[11px] font-mono-display text-[var(--ink-muted)]"
              >
                Updated {updatedLabel}
              </span>
            )}
          </div>

          {/* Sort toggle. Each button wears the colour of the pill it
              sorts by (see SORT_OPTIONS) so the mapping is readable at
              a glance rather than something the editor has to learn. */}
          <div
            data-testid="newsletter-sort-toggle"
            className="inline-flex flex-wrap border border-[var(--border)] text-[11px] font-mono-display uppercase tracking-wider"
          >
            {SORT_OPTIONS.map((opt, i) => {
              const disabled = opt.key === "jetpack" && !jetpackConfigured;
              return (
                <button
                  key={opt.key}
                  type="button"
                  data-testid={opt.testId}
                  onClick={() => setSortBy(opt.key)}
                  disabled={disabled}
                  title={
                    disabled
                      ? "Jetpack stats are not configured for this property yet"
                      : undefined
                  }
                  className={`px-2.5 py-1 ${
                    i > 0 ? "border-l border-[var(--border)]" : ""
                  } ${
                    sortBy === opt.key
                      ? opt.active
                      : "hover:bg-[var(--secondary)]"
                  } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <p className="text-xs text-[var(--ink-muted)] mb-4">
        {subtitleRange} · {subtitleSort}.
      </p>

      {isLoading && (
        <div className="text-sm text-[var(--ink-muted)] flex items-center gap-2 py-4">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading candidates…
        </div>
      )}
      {!isLoading && isFetching && (
        <div className="text-[11px] text-[var(--ink-muted)] flex items-center gap-2 pb-2">
          <Loader2 className="w-3 h-3 animate-spin" /> Updating range…
        </div>
      )}
      {!isLoading && sortedCandidates.length === 0 && (
        <div
          data-testid="newsletter-empty"
          className="text-sm text-[var(--ink-muted)] py-4"
        >
          {hideUsed && candidates.length > 0
            ? "Every article in this range has already been drafted or sent — widen the window or clear the filter."
            : "No published articles in this range — try a wider window."}
        </div>
      )}
      <ul className="divide-y divide-[var(--border)]">
        {pageItems.map((c) => {
          const picked = selectedIds.has(c.article_id);
          return (
            <li
              key={c.article_id}
              data-testid={`newsletter-candidate-${c.article_id}`}
              className="flex items-center gap-3 py-2.5"
            >
              <button
                type="button"
                onClick={() =>
                  picked ? onRemove(c.article_id) : onAdd(c)
                }
                data-testid={`newsletter-toggle-${c.article_id}`}
                className={`w-5 h-5 border flex items-center justify-center shrink-0 ${
                  picked
                    ? "bg-[var(--accent)] border-[var(--accent)] text-white"
                    : "border-[var(--border-strong)]"
                }`}
              >
                {picked && <Check className="w-3 h-3" />}
              </button>
              {/* Pills run in the same order as the sort buttons and
                  share their colours (SORT_OPTIONS): amber = Jetpack
                  website views, indigo = Facebook views, emerald =
                  Facebook reactions + comments. */}
              <MetricPill
                testId={`newsletter-jetpack-views-${c.article_id}`}
                value={c.jetpack_views ?? null}
                classes={SORT_BY_KEY.jetpack.pill}
                title={
                  c.jetpack_views == null
                    ? (jetpackConfigured
                        ? "Website views unavailable — Jetpack has no data for this post yet"
                        : "Jetpack stats are not configured for this property yet")
                    : `${c.jetpack_views} website views in the last ${jetpackWindowDays} days (Jetpack)`
                }
              />
              {/* `views` is nullable — a literal null means "insights
                  unavailable" (see meta_service.get_fb_post_metrics:
                  page < 100 followers, missing `read_insights`, or
                  fresh post pre-daily-rollup). */}
              <MetricPill
                testId={`newsletter-views-${c.article_id}`}
                value={c.views ?? null}
                classes={SORT_BY_KEY.fbviews.pill}
                title={
                  c.views == null
                    ? "Facebook views unavailable — reconnect the page or wait for the daily insights rollup"
                    : `${c.views} Facebook views`
                }
              />
              <MetricPill
                testId={`newsletter-engagement-${c.article_id}`}
                value={c.engagement ?? 0}
                classes={SORT_BY_KEY.engagement.pill}
                title={`${c.engagement} reactions + comments`}
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-[var(--ink)] truncate">
                  {c.headline}
                </div>
                {/* On narrow screens the date drops beneath the headline
                    instead of getting squeezed off-screen. On wider
                    screens it's rendered as a right-aligned column
                    (see the sm:hidden / hidden sm:inline pair). */}
                <div
                  data-testid={`newsletter-candidate-date-mobile-${c.article_id}`}
                  className="text-[11px] text-[var(--ink-muted)] sm:hidden mt-0.5"
                >
                  {formatShortDate(c.published_at)}
                </div>
              </div>
              <UsageBadge
                usage={resolveUsage(c).usage}
                clearedAt={resolveUsage(c).clearedAt}
                articleId={c.article_id}
                busy={usageBusyId === c.article_id}
                onToggle={(cleared) =>
                  toggleUsageCleared(c.article_id, cleared)
                }
              />
              <span
                data-testid={`newsletter-candidate-date-${c.article_id}`}
                className="hidden sm:inline shrink-0 text-[11px] text-[var(--ink-muted)] tabular-nums"
              >
                {formatShortDate(c.published_at)}
              </span>
              {c.wordpress_url && (
                <a
                  href={c.wordpress_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[var(--ink-muted)] hover:text-[var(--accent)]"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
            </li>
          );
        })}
      </ul>

      {sortedCandidates.length > PAGE_SIZE && (
        <div
          data-testid="newsletter-pagination"
          className="flex items-center justify-between mt-4 pt-3 border-t border-[var(--border)] text-[11px] font-mono-display uppercase tracking-wider text-[var(--ink-muted)]"
        >
          <button
            type="button"
            data-testid="newsletter-page-prev"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="inline-flex items-center gap-1 px-2 py-1 hover:text-[var(--ink)] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-3 h-3" />
            Previous
          </button>
          <span data-testid="newsletter-page-label">
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            data-testid="newsletter-page-next"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="inline-flex items-center gap-1 px-2 py-1 hover:text-[var(--ink)] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Next
            <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
}
